import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import {
  useArchivePromotion,
  useCampaigns,
  useEndPromotion,
  usePausePromotion,
  usePromotions,
  useProductSets,
} from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { buildPromotionSummary } from '../../lib/promoSummary';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import type { ToastSeverity } from '../../lib/useToast';
import { PROMOTION_STATUS_COLOR } from '../../model/statuses';
import type { PromotionStatus, PromotionSummary, PromotionType } from '../../model/types';

const STATUSES: PromotionStatus[] = [
  'DRAFT',
  'PENDING_APPROVAL',
  'SCHEDULED',
  'ACTIVE',
  'PAUSED',
  'ENDED',
  'ARCHIVED',
  'REJECTED',
];
const TYPES: PromotionType[] = ['PRODUCT', 'CART', 'SHIPPING'];

export interface PromotionsListPanelProps {
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * "Promotions" — the promotions list (plan-011 §8.4). The BFF's `listPromotions` only supports a
 * server-side `status` filter (plus page/size) — type/campaign filters and free-text search are
 * applied client-side over the loaded page (a larger page size keeps that useful).
 */
export function PromotionsListPanel({ onResult }: PromotionsListPanelProps) {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const { canManage } = usePromoPermissions();
  const success = (message: string) => onResult('success', message);
  const notifyError = (message: string) => onResult('error', message);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PromotionStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<PromotionType | ''>('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [menuTarget, setMenuTarget] = useState<{ anchor: HTMLElement; promotion: PromotionSummary } | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ action: 'end' | 'archive'; promotion: PromotionSummary } | null>(
    null,
  );

  const query = usePromotions({ status: statusFilter || undefined, page: 0, size: 200 });
  const campaigns = useCampaigns({ size: 200 });
  const productSets = useProductSets({ size: 200 });

  const pauseMutation = usePausePromotion();
  const endMutation = useEndPromotion();
  const archiveMutation = useArchivePromotion();

  const campaignName = (campaignId: string | null | undefined) =>
    campaigns.data?.content.find((c) => c.id === campaignId)?.name;
  const resolveProductSetName = (id: string) => productSets.data?.content.find((s) => s.id === id)?.name;

  const filtered = useMemo(() => {
    const all = query.data?.content ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q) && !p.code.toLowerCase().includes(q)) return false;
      if (typeFilter && p.type !== typeFilter) return false;
      if (campaignFilter && p.campaignId !== campaignFilter) return false;
      return true;
    });
  }, [query.data, search, typeFilter, campaignFilter]);

  const pageItems = filtered.slice(page * size, page * size + size);
  const hasFilters = Boolean(search || statusFilter || typeFilter || campaignFilter);

  if (query.isLoading) return <LoadingState label={t('promotionsList.loading')} />;
  if (query.isError) {
    return (
      <ErrorState
        title={t('promotionsList.loadError.title')}
        message={query.error?.message ?? t('common:error.unknown')}
        onRetry={() => void query.refetch()}
      />
    );
  }

  if ((query.data?.content.length ?? 0) === 0) {
    return (
      <EmptyState
        icon={<LocalOfferOutlinedIcon sx={{ fontSize: 48 }} />}
        title={t('promotionsList.empty.title')}
        description={t('promotionsList.empty.description')}
        action={
          canManage ? (
            <Button variant="contained" startIcon={<AddRoundedIcon />} component={RouterLink} to="/promo/promotions/new">
              {t('promotionsList.newPromotion')}
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          size="small"
          placeholder={t('promotionsList.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label={t('promotionsList.statusFilterLabel')}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as PromotionStatus | '');
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('promotionsList.allStatuses')}</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('promotionsList.typeFilterLabel')}
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as PromotionType | '');
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('promotionsList.allTypes')}</MenuItem>
          {TYPES.map((ty) => (
            <MenuItem key={ty} value={ty}>
              {t(`builder.type.${ty}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('promotionsList.campaignFilterLabel')}
          value={campaignFilter}
          onChange={(e) => {
            setCampaignFilter(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('promotionsList.allCampaigns')}</MenuItem>
          {(campaigns.data?.content ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <Stack sx={{ flex: 1 }} />
        {canManage ? (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            component={RouterLink}
            to="/promo/promotions/new"
            sx={{ flexShrink: 0 }}
          >
            {t('promotionsList.newPromotion')}
          </Button>
        ) : null}
      </Stack>

      {pageItems.length === 0 && hasFilters ? (
        <EmptyState
          title={t('promotionsList.noMatches.title')}
          description={t('promotionsList.noMatches.description')}
          action={
            <Button
              size="small"
              onClick={() => {
                setSearch('');
                setStatusFilter('');
                setTypeFilter('');
                setCampaignFilter('');
              }}
            >
              {t('promotionsList.resetFilters')}
            </Button>
          }
        />
      ) : (
        <TablePageShell
          footer={
            <CompactTablePagination
              count={filtered.length}
              page={page}
              rowsPerPage={size}
              onPageChange={(_e, next) => setPage(next)}
              onRowsPerPageChange={(e) => {
                setSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
            />
          }
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('promotionsList.table.name')}</TableCell>
                  <TableCell>{t('promotionsList.table.campaign')}</TableCell>
                  <TableCell>{t('promotionsList.table.type')}</TableCell>
                  <TableCell>{t('promotionsList.table.mechanics')}</TableCell>
                  <TableCell>{t('promotionsList.table.period')}</TableCell>
                  <TableCell align="right">{t('promotionsList.table.priority')}</TableCell>
                  <TableCell>{t('promotionsList.table.status')}</TableCell>
                  <TableCell align="right">{t('promotionsList.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageItems.map((p) => (
                  <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/promo/promotions/${p.id}`)}>
                    <TableCell>
                      <Typography variant="body2">{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {p.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{campaignName(p.campaignId) ?? t('promotionsList.noCampaign')}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={t(`builder.type.${p.type}`)} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {/* Summary needs full conditions/actions — not on the list-summary DTO;
                            requires-coupon + type are the only signals it carries, so degrade to
                            that rather than fetching each row's full detail (N+1) just for the list. */}
                        {buildPromotionSummary(
                          t,
                          { type: p.type, actions: [], conditions: [], requiresCoupon: p.requiresCoupon },
                          { resolveProductSetName },
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {p.validFrom ? formatDateTime(p.validFrom) : t('promotionsList.noStart')}
                      {' – '}
                      {p.validTo ? formatDateTime(p.validTo) : t('promotionsList.noEnd')}
                    </TableCell>
                    <TableCell align="right">{p.priority}</TableCell>
                    <TableCell>
                      <Chip size="small" color={PROMOTION_STATUS_COLOR[p.status]} label={p.status} />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        aria-label={t('promotionsList.rowMenuAria')}
                        onClick={(e) => setMenuTarget({ anchor: e.currentTarget, promotion: p })}
                      >
                        <MoreVertRoundedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}

      <Menu anchorEl={menuTarget?.anchor} open={Boolean(menuTarget)} onClose={() => setMenuTarget(null)}>
        <MenuItem
          onClick={() => {
            if (menuTarget) navigate(`/promo/promotions/${menuTarget.promotion.id}`);
            setMenuTarget(null);
          }}
        >
          {t('common:action.edit')}
        </MenuItem>
        {canManage && menuTarget && (menuTarget.promotion.status === 'ACTIVE' || menuTarget.promotion.status === 'SCHEDULED') ? (
          <MenuItem
            onClick={() => {
              if (!menuTarget) return;
              pauseMutation.mutate(
                { promotionId: menuTarget.promotion.id },
                { onSuccess: () => success(t('builder.paused')), onError: (e) => notifyError(e.message) },
              );
              setMenuTarget(null);
            }}
          >
            {t('builder.pause')}
          </MenuItem>
        ) : null}
        {canManage && menuTarget && (menuTarget.promotion.status === 'ACTIVE' || menuTarget.promotion.status === 'PAUSED' || menuTarget.promotion.status === 'SCHEDULED') ? (
          <MenuItem
            onClick={() => {
              if (menuTarget) setConfirmTarget({ action: 'end', promotion: menuTarget.promotion });
              setMenuTarget(null);
            }}
          >
            {t('builder.end')}
          </MenuItem>
        ) : null}
        {canManage && menuTarget && menuTarget.promotion.status !== 'ARCHIVED' ? (
          <MenuItem
            onClick={() => {
              if (menuTarget) setConfirmTarget({ action: 'archive', promotion: menuTarget.promotion });
              setMenuTarget(null);
            }}
          >
            {t('builder.archive')}
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog open={Boolean(confirmTarget)} onClose={() => setConfirmTarget(null)}>
        <DialogTitle>
          {confirmTarget?.action === 'end' ? t('builder.endDialog.title') : t('builder.archiveDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmTarget?.action === 'end' ? t('builder.endDialog.body') : t('builder.archiveDialog.body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (!confirmTarget) return;
              const mutation = confirmTarget.action === 'end' ? endMutation : archiveMutation;
              mutation.mutate(confirmTarget.promotion.id, {
                onSuccess: () => {
                  success(confirmTarget.action === 'end' ? t('builder.ended') : t('builder.archived'));
                  setConfirmTarget(null);
                },
                onError: (e) => notifyError(e.message),
              });
            }}
          >
            {confirmTarget?.action === 'end' ? t('builder.end') : t('builder.archive')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
